import React, { useState, useEffect } from "react"
import styled, { keyframes } from "styled-components"
import { useIntl } from "gatsby-plugin-intl"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/pro-regular-svg-icons"

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 500px;
  max-width: 100%;
`

const HeaderSection = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Branding = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const LogoSphere = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #00d2ff 0%, #3a7bd5 100%);
  box-shadow: 0 4px 10px rgba(58, 123, 213, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 6px;
    left: 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%);
  }
`

const Title = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  min-height: 250px;
`

const SphereWrapper = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  margin-bottom: 28px;
  animation: ${float} 2s ease-in-out infinite;
`

const Sphere = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #00d2ff 0%, #3a7bd5 100%);
  box-shadow: 0 8px 24px rgba(58, 123, 213, 0.35);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 10px;
    left: 16px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%);
  }
`

const OrbitRing = styled.div`
  position: absolute;
  top: -8px;
  left: -8px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: rgba(0, 210, 255, 0.4);
  border-right-color: rgba(58, 123, 213, 0.2);
  animation: ${spin} 2s linear infinite;
`

const DotsRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00d2ff, #3a7bd5);
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
`

const StatusText = styled.p`
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
  margin: 0;
  min-height: 24px;
  text-align: center;
  transition: opacity 0.3s ease;
`

const ShimmerBar = styled.div`
  width: 180px;
  height: 4px;
  border-radius: 2px;
  margin-top: 16px;
  background: linear-gradient(
    90deg,
    rgba(0, 210, 255, 0.1) 0%,
    rgba(58, 123, 213, 0.4) 50%,
    rgba(0, 210, 255, 0.1) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`

const MESSAGES = [
  { id: "trustai::loading::analyzing", defaultMessage: "Analyzing your campaign..." },
  { id: "trustai::loading::crafting", defaultMessage: "Crafting your story..." },
  { id: "trustai::loading::optimizing", defaultMessage: "Optimizing for impact..." },
  { id: "trustai::loading::polishing", defaultMessage: "Polishing the details..." },
  { id: "trustai::loading::generating", defaultMessage: "Generating your content..." },
]

const TRANSLATE_MESSAGES = [
  { id: "trustai::loading::translating", defaultMessage: "Translating your content..." },
  { id: "trustai::loading::localizing", defaultMessage: "Localizing for each language..." },
  { id: "trustai::loading::finalizing", defaultMessage: "Finalizing translations..." },
]

const TrustAILoading = ({ variant = "generate" }) => {
  const { formatMessage } = useIntl()
  const [messageIndex, setMessageIndex] = useState(0)

  const messages = variant === "translate" ? TRANSLATE_MESSAGES : MESSAGES

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length)
    }, 2500)
    return () => clearInterval(interval)
  }, [messages.length])

  return (
    <Container>
      <HeaderSection>
        <TitleRow>
          <Branding>
            <LogoSphere />
            <Title>{formatMessage({ id: "trustai::studio::title", defaultMessage: "TrustAI Studio" })}</Title>
          </Branding>
        </TitleRow>
      </HeaderSection>

      <LoadingContent>
        <SphereWrapper>
          <Sphere />
          <OrbitRing />
        </SphereWrapper>

        <DotsRow>
          <Dot delay="0s" />
          <Dot delay="0.2s" />
          <Dot delay="0.4s" />
        </DotsRow>

        <StatusText>
          {formatMessage(messages[messageIndex])}
        </StatusText>

        <ShimmerBar />
      </LoadingContent>
    </Container>
  )
}

export default TrustAILoading
